import { createContext } from "react";
import { io } from "socket.io-client";
import { isExpired } from "../../utils/isExpired";
import socketMetricsService from "../../services/SocketMetricsService";

// ✅ FIX 03/12/2025: Función para renovar token proactivamente antes de recargar
const tryRefreshToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return false;
  
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    
    const response = await fetch(`${backendUrl}/auth/refresh_token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      console.log("✅ Token renovado exitosamente en SocketContext");
      return true;
    }
    return false;
  } catch (error) {
    console.error("❌ Error renovando token en SocketContext:", error);
    return false;
  }
};

// Socket.IO retains listeners and buffers emits while disconnected. Each wrapper
// owns only its handlers; components rejoin rooms through connect/ready.
class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.socket = socketManager.currentSocket;
    this.registeredListeners = new Map();
    this.isMounted = true;
  }

  on(event, callback) {
    if (!this.isMounted) return;
    let handlers = this.registeredListeners.get(event);
    if (!handlers) {
      handlers = new Set();
      this.registeredListeners.set(event, handlers);
    }
    if (handlers.has(callback)) return;
    handlers.add(callback);
    this.socket.on(event, callback);
  }

  off(event, callback) {
    const handlers = this.registeredListeners.get(event);
    if (!handlers) return;
    for (const handler of handlers) {
      if (!callback || handler === callback) {
        this.socket.off(event, handler);
        handlers.delete(handler);
      }
    }
    if (!handlers.size) this.registeredListeners.delete(event);
  }

  emit(event, ...params) {
    if (this.isMounted) this.socket.emit(event, ...params);
  }

  disconnect() {
    this.isMounted = false;
    this.registeredListeners.forEach((handlers, event) => {
      handlers.forEach(handler => this.socket.off(event, handler));
    });
    this.registeredListeners.clear();
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,
  /** Suscriptores al evento engine `connect` (incluye reconexiones). M6: resync presencia en /users */
  _engineConnectSubscribers: new Set(),

  subscribeConnect: function (callback) {
    if (typeof callback !== "function") return () => {};
    this._engineConnectSubscribers.add(callback);
    if (this.currentSocket && this.currentSocket.connected) {
      queueMicrotask(() => {
        if (this._engineConnectSubscribers.has(callback)) {
          try {
            callback();
          } catch (e) {
            console.warn("subscribeConnect callback:", e);
          }
        }
      });
    }
    return () => {
      this._engineConnectSubscribers.delete(callback);
    };
  },

  _notifyEngineConnect: function () {
    this._engineConnectSubscribers.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        console.warn("subscribeConnect:", e);
      }
    });
  },

  getSocket: function(companyId) {
    // ✅ MÉTODO PARA DESCONECTAR MANUALMENTE CUANDO SEA NECESARIO
    this.disconnectSocket = function() {
      if (this.currentSocket) {
        console.warn("Desconectando socket manualmente");
        this.currentSocket.disconnect();
        this.currentSocket = null;
        this.currentCompanyId = null;
        this.currentUserId = null;
      }
    };

    // ✅ MÉTODO PARA LIMPIAR LISTENERS SIN DESCONECTAR
    this.cleanupListeners = function() {
      if (this.currentSocket) {
        console.warn("Limpiando listeners del socket");
        this.currentSocket.removeAllListeners();
      }
    };
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        console.warn("closing old socket - company or user changed");
        try {
          // ✅ CORREGIDO: Limpiar listeners y desconectar correctamente antes de nullificar
          this.currentSocket.removeAllListeners();
          // ✅ Desconectar el socket antes de nullificar para evitar errores en listeners activos
          if (this.currentSocket.connected) {
            this.currentSocket.disconnect();
          }
        } catch (error) {
          console.warn("Error limpiando socket anterior:", error);
        }
        this.currentSocket = null;
        this.currentCompanyId = null;
        this.currentUserId = null;
      }

      let token = localStorage.getItem("token");
      if (!token) {
        // Solo mostrar warning en desarrollo para evitar spam en producción
        console.debug("No token found, returning dummy socket");
        return new DummySocket();
      }
      
      if ( isExpired(token) ) {
        console.warn("Token expirado detectado, intentando renovar...");
        // ✅ FIX 03/12/2025: Intentar renovar token antes de recargar
        tryRefreshToken().then((renewed) => {
          if (renewed) {
            console.log("✅ Token renovado, recargando para usar nuevo token...");
        setTimeout(() => {
          window.location.reload();
            }, 500);
          } else {
            console.warn("❌ No se pudo renovar token, redirigiendo al login");
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            window.location.href = "/login";
          }
        });
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      
      // Alinear con servidor: pings frecuentes = menos idle en proxy/NAT (evita cortes ~5 min)
      const socketConfig = {
        transports: ["websocket", "polling"], // Fallback a polling si WebSocket falla
        pingTimeout: 60000,
        pingInterval: 25000,
        upgradeTimeout: 10000, // 10 segundos
        // En móvil el SO suspende la pestaña: con un límite de intentos el socket
        // quedaba muerto y los mensajes no aparecían hasta recargar.
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
        autoConnect: true,
        query: { token },
        // ✅ CONFIGURACIÓN ADICIONAL PARA ESTABILIDAD
        forceNew: false, // Reutilizar conexión existente
        multiplex: true, // Multiplexar conexiones
        rememberUpgrade: true, // Recordar upgrade de transporte
        // ✅ MEJORADO: Configuración para evitar congelamiento
        closeOnBeforeunload: true, // Cerrar socket antes de recargar página
        withCredentials: false, // Evitar problemas de CORS
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      this.currentSocket = io(backendUrl, socketConfig);

      // ✅ CORREGIDO: Validar que currentSocket e io existan antes de acceder
      if (this.currentSocket && this.currentSocket.io) {
        this.currentSocket.io.on("reconnect_attempt", async () => {
          // ✅ VALIDAR QUE EL SOCKET SIGA EXISTIENDO ANTES DE ACCEDER A SUS PROPIEDADES
          if (!this.currentSocket || !this.currentSocket.io) {
            console.warn("⚠️ Socket cerrado durante reconexión, cancelando intento");
            return;
          }
          
          this.currentSocket.io.opts.query.r = 1;
          token = localStorage.getItem("token");
          if ( isExpired(token) ) {
            console.warn("Token expirado durante reconexión, intentando renovar...");
            // ✅ FIX 03/12/2025: Intentar renovar token antes de redirigir al login
            const renewed = await tryRefreshToken();
            if (renewed) {
              // ✅ VALIDAR NUEVAMENTE ANTES DE ACTUALIZAR (el socket puede haberse cerrado durante la renovación)
              if (this.currentSocket && this.currentSocket.io && this.currentSocket.io.opts) {
                token = localStorage.getItem("token");
                this.currentSocket.io.opts.query.token = token;
                console.log("✅ Token renovado durante reconexión");
                socketMetricsService.trackReconnection();
              }
            } else {
              console.warn("❌ No se pudo renovar token, redirigiendo al login");
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
              window.location.href = "/login";
            }
          } else {
            console.warn("Reconectando socket...");
            // 📊 FASE 0: Tracking de reconexión para métricas
            socketMetricsService.trackReconnection();
          }
        });
      }
      
      this.currentSocket.on("disconnect", (reason) => {
        // 📊 FASE 0: Tracking de desconexión para métricas
        socketMetricsService.trackDisconnection(reason);
        
        // Solo mostrar en desarrollo para evitar spam en producción
        if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') || (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === undefined)) {
          console.debug(`Socket desconectado: ${reason}`);
        }
        
        if (reason.startsWith("io server disconnect")) {
          console.warn("Socket desconectado por el servidor");
          token = localStorage.getItem("token");
          
          if ( isExpired(token) ) {
            console.warn("Token expirado durante desconexión del servidor, intentando renovar...");
            // ✅ FIX 03/12/2025: Intentar renovar token antes de redirigir al login
            tryRefreshToken().then((renewed) => {
              if (renewed) {
                console.log("✅ Token renovado, intentando reconectar...");
                // El socket intentará reconectar automáticamente con el nuevo token
              } else {
                console.warn("❌ No se pudo renovar token, redirigiendo al login");
            localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
            window.location.href = "/login";
              }
            });
            return;
          }
          console.warn("Intentando reconectar socket...");
          return;
        }        
      });
      
      this.currentSocket.on("connect", (...params) => {
        this._notifyEngineConnect();
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
          console.debug("socket connected", params);
        }
        // ✅ RE-REGISTRAR LISTENERS DESPUÉS DE RECONEXIÓN
        this.socketReady = false;
        this.onReady(() => {
          this.socketReady = true;
          if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
            console.debug("✅ Socket listo para usar");
          }
        });
      });

      this.currentSocket.on("connect_error", (error) => {
        // ✅ MEJORADO: Evitar spam de errores que congela la aplicación
        // Solo mostrar error si no es un error de transporte común (que se resuelve automáticamente)
        if (error.message && !error.message.includes("websocket error") && !error.message.includes("TransportError")) {
          console.error("Socket connection error:", error);
        } else {
          // Errores de transporte son comunes y se resuelven automáticamente, solo log en debug
          if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
            console.debug("Socket transport error (se resolverá automáticamente):", error.message);
          }
        }
        
        // ✅ MEJORADO: Limitar intentos de reconexión para evitar congelamiento
        if (this.reconnectionCount >= this.maxReconnections) {
          console.warn(`⚠️ Máximo de intentos de reconexión alcanzado (${this.maxReconnections}). Deteniendo reconexión automática.`);
          this.currentSocket.io.reconnecting = false; // Detener reconexión automática
        }
      });
      
      // ✅ LISTENERS PARA HEARTBEAT
      this.currentSocket.on("heartbeat", () => {
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
          console.debug("💓 Heartbeat recibido del servidor");
        }
        // Responder al heartbeat
        this.currentSocket.emit("heartbeat");
      });
      
      this.currentSocket.on("pong", () => {
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
          console.debug("🏓 Pong recibido del servidor");
        }
      });
      
      // ✅ SOLO LOGGING EN DESARROLLO PARA EVITAR SPAM
      // ✅ Además: nunca depender de `process` (puede faltar en algunos bundles)
      const isDev =
        (typeof window !== "undefined" && window.location && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) ||
        (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "development");

      if (isDev) {
        this.currentSocket.onAny((event, ...args) => {
          console.debug("Event: ", { socket: this.currentSocket, event, args });
        });
      }
      
      this.onReady(() => {
        this.socketReady = true;
      });

      this._installWakeHandlers();
    }
    
    return new ManagedSocket(this);
  },
  
  onReady: function( callbackReady ) {
    if (this.socketReady) {
      callbackReady();
      return
    }
    
    this.currentSocket.once("ready", () => {
      callbackReady();
    });
  },
  
  onConnect: function( callbackReady ) { this.onReady( callbackReady ) },

  _wakeHandlersInstalled: false,
  _installWakeHandlers: function () {
    if (this._wakeHandlersInstalled || typeof window === "undefined") return;
    this._wakeHandlersInstalled = true;
    let hiddenAt = 0;
    let lastResumeAt = 0;
    const wake = (fromHide) => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const s = this.currentSocket;
      const away = hiddenAt && Date.now() - hiddenAt > 8000;
      if (s && (!s.connected || (fromHide && away))) {
        try {
          if (s.connected) s.disconnect();
          s.connect();
        } catch (e) {
          console.warn("Socket wake reconnect:", e);
        }
      }
      hiddenAt = 0;
      // Focus, visibility and online may arrive together after mobile suspension.
      if (Date.now() - lastResumeAt > 250) {
        lastResumeAt = Date.now();
        window.dispatchEvent(new CustomEvent("watoolx-resume"));
      }
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      wake(true);
    });
    window.addEventListener("online", () => wake(false));
    window.addEventListener("focus", () => wake(false));
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) hiddenAt = Date.now() - 2000;
      wake(true);
    });
  },

};

const SocketContext = createContext()

export { SocketContext, SocketManager };
